import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { LayoutGrid, List as ListIcon, Heart, X, SlidersHorizontal } from "lucide-react";
import { useLang } from "@/context";
import { T, formatPrice, formatSurface } from "@/i18n";
import { ZONES } from "@/data/zones";
import { PropertyCard } from "@/components/PropertyCard";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/motion";
import api from "@/api";

const FAV_KEY = "iim_favorites";

export default function Listings({ preset }) {
  const { lang } = useLang();
  const t = T[lang];
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("grid");
  const [showFilters, setShowFilters] = useState(false);
  const [favs, setFavs] = useState(() => JSON.parse(localStorage.getItem(FAV_KEY) || "[]"));
  const [favsOnly, setFavsOnly] = useState(false);
  const [compare, setCompare] = useState([]);
  const [showCompare, setShowCompare] = useState(false);
  const [filters, setFilters] = useState({
    type: preset === "houses" ? "house,villa" : preset === "beachfront" || preset === "farmland" ? preset : searchParams.get("type") || "",
    zone: searchParams.get("zone") || "",
    min_price: searchParams.get("min_price") || "",
    max_price: searchParams.get("max_price") || "",
    min_surface: "",
    status: preset === "sold" ? "sold" : "",
    sort: "",
  });

  const fetchItems = useCallback(() => {
    setLoading(true);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get("/listings", { params }).then((r) => setItems(r.data)).catch(() => setItems([])).finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const toggleFav = (item) => {
    const next = favs.includes(item.ref_code) ? favs.filter((c) => c !== item.ref_code) : [...favs, item.ref_code];
    setFavs(next);
    localStorage.setItem(FAV_KEY, JSON.stringify(next));
  };
  const toggleCompare = (item) => {
    setCompare((c) => {
      if (c.find((x) => x.ref_code === item.ref_code)) return c.filter((x) => x.ref_code !== item.ref_code);
      if (c.length >= 3) { alert(t.listings.compareMax); return c; }
      return [...c, item];
    });
  };

  const visible = favsOnly ? items.filter((i) => favs.includes(i.ref_code)) : items;
  const title = preset ? t.listings.presetTitles[preset] : t.listings.title;
  const sub = preset ? t.listings.presetSubs[preset] : t.listings.sub;

  const filterControls = (
    <div className="space-y-4">
      <div>
        <label className="label-site">{t.listings.type}</label>
        <select className="input-site" value={filters.type} onChange={(e) => setF("type", e.target.value)} data-testid="filter-type" disabled={!!preset && preset !== "sold"}>
          <option value="">{t.common.all}</option>
          {Object.entries(t.types).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      <div>
        <label className="label-site">{t.listings.zone}</label>
        <select className="input-site" value={filters.zone} onChange={(e) => setF("zone", e.target.value)} data-testid="filter-zone">
          <option value="">{t.common.all}</option>
          {ZONES.map((z) => <option key={z.slug} value={z.slug}>{z.name}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label-site">{t.listings.priceMin}</label>
          <input type="number" className="input-site" value={filters.min_price} onChange={(e) => setF("min_price", e.target.value)} data-testid="filter-price-min" />
        </div>
        <div>
          <label className="label-site">{t.listings.priceMax}</label>
          <input type="number" className="input-site" value={filters.max_price} onChange={(e) => setF("max_price", e.target.value)} data-testid="filter-price-max" />
        </div>
      </div>
      <div>
        <label className="label-site">{t.listings.surfaceMin}</label>
        <input type="number" className="input-site" value={filters.min_surface} onChange={(e) => setF("min_surface", e.target.value)} data-testid="filter-surface-min" />
      </div>
      {!preset && (
        <div>
          <label className="label-site">{t.listings.statusLabel}</label>
          <select className="input-site" value={filters.status} onChange={(e) => setF("status", e.target.value)} data-testid="filter-status">
            <option value="">{t.common.all}</option>
            <option value="available">{t.common.available}</option>
            <option value="reserved">{t.common.reserved}</option>
            <option value="sold">{t.common.sold}</option>
          </select>
        </div>
      )}
      <label className="flex items-center gap-2.5 text-sm cursor-pointer">
        <input type="checkbox" checked={favsOnly} onChange={(e) => setFavsOnly(e.target.checked)} className="h-4 w-4 accent-[#B5472B]" data-testid="filter-favs-only" />
        <Heart size={14} className="text-[#B5472B]" /> {t.listings.showFavs}
      </label>
      <button onClick={() => { setFilters({ type: preset === "houses" ? "house,villa" : preset === "beachfront" || preset === "farmland" ? preset : "", zone: "", min_price: "", max_price: "", min_surface: "", status: preset === "sold" ? "sold" : "", sort: "" }); setFavsOnly(false); }}
        className="text-sm text-[#B5472B] font-medium hover:underline" data-testid="filter-reset">{t.listings.reset}</button>
    </div>
  );

  return (
    <div className="container-site py-28 md:py-36" data-testid="listings-page">
      <SEO title={`${title} — ${t.brand}`} description={sub} lang={lang} pathKey="properties" />
      <Reveal>
        <p className="eyebrow">{t.nav.properties}</p>
        <h1 className="h2-serif mt-3">{title}</h1>
        <p className="mt-4 text-[#1F2328]/70 max-w-2xl">{sub}</p>
      </Reveal>

      <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
        <button onClick={() => setShowFilters(!showFilters)} className="btn-outline !py-2 lg:hidden" data-testid="filters-toggle">
          <SlidersHorizontal size={15} /> {t.listings.filters}
        </button>
        <p className="text-sm text-[#8A8F96]" data-testid="results-count">{visible.length} {t.common.results}</p>
        <div className="flex items-center gap-3">
          <select className="input-site !w-auto !py-2" value={filters.sort} onChange={(e) => setF("sort", e.target.value)} data-testid="sort-select" aria-label="Sort">
            <option value="">{t.listings.sortNewest}</option>
            <option value="price_asc">{t.listings.sortPriceAsc}</option>
            <option value="price_desc">{t.listings.sortPriceDesc}</option>
            <option value="surface_desc">{t.listings.sortSurface}</option>
          </select>
          <div className="flex rounded-full border border-[#0E4D5C]/20 overflow-hidden">
            <button onClick={() => setView("grid")} className={`p-2.5 ${view === "grid" ? "bg-[#0E4D5C] text-white" : "text-[#0E4D5C]"}`} aria-label={t.common.grid} data-testid="view-grid"><LayoutGrid size={16} /></button>
            <button onClick={() => setView("list")} className={`p-2.5 ${view === "list" ? "bg-[#0E4D5C] text-white" : "text-[#0E4D5C]"}`} aria-label={t.common.list} data-testid="view-list"><ListIcon size={16} /></button>
          </div>
        </div>
      </div>

      <div className="mt-8 grid lg:grid-cols-4 gap-10">
        <aside className={`${showFilters ? "block" : "hidden"} lg:block card-site p-6 h-fit lg:sticky lg:top-24`} data-testid="filters-panel">
          {filterControls}
        </aside>
        <div className="lg:col-span-3">
          {loading ? (
            <div className="grid gap-8 md:grid-cols-2">{[0, 1, 2, 3].map((i) => <div key={i} className="h-80 rounded-2xl bg-[#0E4D5C]/5 animate-pulse" />)}</div>
          ) : visible.length === 0 ? (
            <div className="card-site p-10 text-center text-[#1F2328]/70" data-testid="empty-state">{t.listings.empty}</div>
          ) : view === "grid" ? (
            <div className="grid gap-8 md:grid-cols-2">
              {visible.map((item) => (
                <PropertyCard key={item.ref_code} item={item}
                  isFav={favs.includes(item.ref_code)} onFav={toggleFav}
                  inCompare={!!compare.find((x) => x.ref_code === item.ref_code)} onCompare={toggleCompare} />
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {visible.map((item) => (
                <div key={item.ref_code} className="card-site p-5 flex flex-wrap items-center gap-5" data-testid={`property-row-${item.ref_code}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-[#8A8F96] uppercase tracking-wider">{t.types[item.type]} · {item.ref_code}</p>
                    <h3 className="font-serif text-lg font-semibold mt-1">{item.title[lang]}</h3>
                    <p className="text-sm text-[#1F2328]/60">{ZONES.find((z) => z.slug === item.zone)?.name} · {formatSurface(item.surface_m2, lang)}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <p className="font-serif text-xl text-[#0E4D5C]">{item.price_on_request ? t.common.priceOnRequest : formatPrice(item.price_eur, lang)}</p>
                    <a href={`/${lang === "en" ? "en/property" : "immobile"}/${item.slug}`} className="btn-primary !py-2 !px-4 !text-sm" data-testid={`row-view-${item.ref_code}`}>{t.common.viewProperty}</a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {compare.length > 0 && (
        <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-40 flex items-center gap-4 rounded-full bg-[#1F2328] text-[#F6EFE4] px-6 py-3 shadow-2xl" data-testid="compare-bar">
          <span className="text-sm">{compare.length} {t.listings.compareBar}</span>
          <button onClick={() => setShowCompare(true)} className="rounded-full bg-[#B5472B] px-5 py-2 text-sm font-medium hover:bg-[#93391f]" data-testid="compare-open">{t.listings.compareNow}</button>
          <button onClick={() => setCompare([])} aria-label={t.common.close} className="text-[#F6EFE4]/60 hover:text-white"><X size={16} /></button>
        </div>
      )}

      {showCompare && (
        <div className="fixed inset-0 z-[70] bg-[#1F2328]/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowCompare(false)} data-testid="compare-modal">
          <div className="bg-[#F6EFE4] rounded-3xl p-6 md:p-8 max-w-4xl w-full max-h-[85vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-2xl text-[#0E4D5C]">{t.listings.compareTitle}</h2>
              <button onClick={() => setShowCompare(false)} aria-label={t.common.close} data-testid="compare-close"><X size={20} /></button>
            </div>
            <div className="mt-6 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#0E4D5C]/10">
                    <th className="text-left py-3 pr-4 text-[#8A8F96] font-medium"></th>
                    {compare.map((c) => <th key={c.ref_code} className="text-left py-3 pr-4 font-serif text-base text-[#1F2328]">{c.title[lang]}</th>)}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#0E4D5C]/10">
                  {[
                    [t.listings.refCode, (c) => c.ref_code],
                    [t.listings.price, (c) => formatPrice(c.price_eur, lang)],
                    [t.listings.surface, (c) => formatSurface(c.surface_m2, lang)],
                    [t.listings.zoneCol, (c) => ZONES.find((z) => z.slug === c.zone)?.name || c.zone],
                    [t.listings.type, (c) => t.types[c.type]],
                    [t.listings.statusLabel, (c) => t.common[c.status] || c.status],
                  ].map(([label, fn]) => (
                    <tr key={label}>
                      <td className="py-3 pr-4 text-[#8A8F96] font-medium">{label}</td>
                      {compare.map((c) => <td key={c.ref_code} className="py-3 pr-4">{fn(c)}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
